/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { Dispatch, KeyboardEvent } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFormContext } from "react-hook-form";
import { z } from "zod";
import { theme, Message, Text, Textarea, Button } from "@nordcloud/gnui";
import { Sidebar } from "~/components";
import { FormGroup, stringRequired } from "~/components/Forms";
import { useDisclosure } from "~/hooks";
import { useGenerateCronFromPhrase } from "~/views/plans/hooks/useGenerateCronFromPhrase/useGenerateCronFromPhrase";
import {
  FormField,
  CronScheduleState,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";

const MAX_PHRASE_LENGTH = 400; // roughly 8 rows
const MIN_PHRASE_LENGTH = 5; // e.g. daily

type Props = {
  updateState: Dispatch<Partial<CronScheduleState>>;
};

export function CronAiGeneratorSidebar({ updateState }: Props) {
  const { isOpen, close, open } = useDisclosure();

  const { setValue } = useFormContext();

  const schema = z.object({
    phrase: stringRequired("phrase")
      .min(MIN_PHRASE_LENGTH, {
        message: `Phrase is too short (min: ${MIN_PHRASE_LENGTH} characters)`,
      })
      .max(MAX_PHRASE_LENGTH, {
        message: `Phrase is too long (max: ${MAX_PHRASE_LENGTH} characters)`,
      })
      .refine((val) => val.trim().length > 0, {
        message: "Phrase cannot be empty or contain only spaces",
      }),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setError,
    getValues,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      phrase: "",
    },
  });

  const { generateCronFromPhrase, loading } = useGenerateCronFromPhrase();

  const onSubmit = async (input: { phrase: string }) => {
    const response = await generateCronFromPhrase(input.phrase);

    if (response.data?.generateCronFromPhrase.error) {
      setError(
        "phrase",
        {
          message: response.data?.generateCronFromPhrase.error,
        },
        {
          shouldFocus: true,
        }
      );
      return;
    }

    setValue(
      FormField.SCHEDULE_CRON,
      response.data?.generateCronFromPhrase.expression,
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    );
    updateState({
      cronExpression: response.data?.generateCronFromPhrase.expression,
    });
    close();
  };

  const closeSidebar = () => {
    close();
    if (getValues().phrase.trim().length === 0) {
      reset();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <>
      <Button type="button" onClick={open} severity="low" icon="sparkles">
        Cron AI Generator
      </Button>
      <Sidebar
        title="Cron AI Generator"
        isOpen={isOpen}
        side="onRight"
        onClick={closeSidebar}
      >
        <Text>
          Provide a clear description of your desired schedule. The AI generator
          will use this information to create the corresponding cron expression.
        </Text>

        <FormGroup
          error={errors["phrase"]}
          customErrorCss={{ position: "unset", transform: "unset" }}
        >
          <Textarea
            onKeyDown={handleKeyDown}
            id="phrase"
            placeholder="e.g. 3rd Friday of every 3 months at 8 AM"
            rows={8}
            {...register("phrase")}
          />
        </FormGroup>
        <Message
          image="warning"
          status="notification"
          css={{
            marginTop: theme.spacing.spacing06,
            paddingTop: theme.spacing.spacing04,
            marginBottom: theme.spacing.spacing06,
            paddingBottom: theme.spacing.spacing04,
          }}
        >
          Please note that the results are generated by AI and may be
          inconsistent
        </Message>
        <Button
          onClick={handleSubmit(onSubmit)}
          initialState={loading ? "loading" : "success"}
          disabled={loading}
        >
          Generate
        </Button>
      </Sidebar>
    </>
  );
}
